import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import api from "../utilis/api";
import { toast } from "react-toastify";

const Checkout = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);
  const { cartItems } = useSelector((state) => state.cart);

  // Shipping options
  const shippingOptions = {
    ground: 7.95,
    threeDay: 14.95,
    secondDay: 24.95,
    nextDay: 39.95,
  };
  const [shippingMethod, setShippingMethod] = useState("ground");

  // Main data object
  const [data, setData] = useState({
    orderItems: [],
    shippingAddress: {
      address: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
      email: "",
      name: "",
      phone: "",
    },
    itemsPrice: 0,
    taxPrice: 0,
    shippingPrice: 0,
    shippingMethod: "ground",
    totalPrice: 0,
  });

  // Prefill user info if logged in
  useEffect(() => {
    if (user) {
      setData((prev) => ({
        ...prev,
        shippingAddress: {
          ...prev.shippingAddress,
          name: user.name || "",
          email: user.email || "",
          phone: user.phone || "",
        },
      }));
    }
  }, [user]);

  // Update order items
  useEffect(() => {
    setData((prev) => ({
      ...prev,
      orderItems: cartItems.map((item) => ({
        productId: item.id,
        variantId: item.selectedVariant?.id,
        name: item.variantTitle
          ? `${item.title} - ${item.variantTitle}`
          : item.title,
        qty: item.quantity,
        price: Number(item.unitPrice || item.variants?.[0]?.price || 0),
        image: item.image,
      })),
    }));
  }, [cartItems]);

  // Calculate prices
  const shippingCost = shippingOptions[shippingMethod] || 0;
  const subtotalPrice = cartItems.reduce(
    (acc, item) =>
      acc + Number(item.unitPrice || item.variants?.[0]?.price || 0) * item.quantity,
    0,
  );
  const total = subtotalPrice + shippingCost;

  useEffect(() => {
    setData((prev) => ({
      ...prev,
      shippingMethod,
      shippingPrice: shippingCost,
      itemsPrice: subtotalPrice,
      totalPrice: total,
    }));
  }, [shippingMethod, shippingCost, subtotalPrice, total]);

  // Handle input changes
  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({
      ...prev,
      shippingAddress: {
        ...prev.shippingAddress,
        [name]: value,
      },
    }));
  };

  // Checkout
  const handleCheckout = async () => {
    if (!cartItems.length) {
      toast.error("Your cart is empty.");
      navigate("/cart");
      return;
    }

    // Validation for guest: all fields must be filled
    if (!user) {
      const { name, email, phone, address, city, state, postalCode, country } =
        data.shippingAddress;
      if (
        !name ||
        !email ||
        !phone ||
        !address ||
        !city ||
        !state ||
        !postalCode ||
        !country
      ) {
        toast.error("Please fill in all required fields.");
        return;
      }
    }

    // Build payload
    const payload = {
      ...data,
      paymentStatus: "Pending",
    };

    if (user) {
      payload.userId = user._id;
    } else {
      payload.guestName = data.shippingAddress.name;
      payload.guestEmail = data.shippingAddress.email;
      payload.guestPhone = data.shippingAddress.phone;
    }

    try {
      const response = await api.post("/orders/process", payload);

      if (response.data?.meta?.success) {
        toast.success(response.data.meta.message, { autoClose: 3000 });
        const orderData = response.data?.data;
        const order = orderData?.order;
        const sessionRes = await api.post("/payments/create-checkout-session", {
          order,
        });

        window.location.href = sessionRes.data.url;
      } else {
        toast.error(response.data?.meta?.message || "Order failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    }
  };

  // Fields definition
  const fields = [
    { label: "Email", name: "email", type: "email", col: 2 },
    { label: "Full Name", name: "name", type: "text", col: 2 },
    { label: "Street Address", name: "address", type: "text", col: 2 },
    { label: "Country", name: "country", type: "text", col: 1 },
    { label: "City", name: "city", type: "text", col: 1 },
    { label: "State", name: "state", type: "text", col: 1 },
    { label: "Zip Code", name: "postalCode", type: "text", col: 1 },
    { label: "Phone Number", name: "phone", type: "tel", col: 2 },
  ];

  return (
    <>
      <Navbar />
      <div className="bg-white w-full py-[1rem]  px-[1rem] sm:px-[5rem] text-[16px] text-gray-500">
        <span className="text-gray-800">
          {" "}
          <Link to={"/"}>Home</Link>
        </span>{" "}
        {">"} Checkout
      </div>
      <div className="py-[2rem] px-[1rem] md:px-[4rem] gap-[2rem]">
        <div className="flex flex-col md:flex-row items-stretch gap-10">
          {/* Left: Shipping */}
          <div className="w-full md:w-1/2">
            <div className="flex flex-col gap-[1rem] p-4">
              <div className="bg-gray-100 w-full px-4 py-3">
                <h1 className="font-bold text-[1.2rem]">Shipping Address</h1>
              </div>
              <form className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {fields.map((field) => (
                  <div key={field.name} className={`md:col-span-${field.col}`}>
                    <label className="block text-sm font-medium mb-1">
                      {field.label}
                    </label>
                    <input
                      type={field.type}
                      name={field.name}
                      placeholder={`Enter your ${field.label.toLowerCase()}`}
                      value={data.shippingAddress[field.name]}
                      onChange={handleAddressChange}
                      disabled={
                        user && ["name", "email", "phone"].includes(field.name)
                      }
                      className={`w-full border border-gray-300 rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-black ${
                        user && ["name", "email", "phone"].includes(field.name)
                          ? "bg-gray-100"
                          : "bg-white"
                      }`}
                    />
                  </div>
                ))}
              </form>
            </div>
          </div>

          {/* Right: Shipping + Summary */}
          <div className="w-full md:w-1/2 flex flex-col gap-6">
            {/* Shipping Method */}
            <div className="bg-gray-100 w-full px-4 py-4 rounded-lg">
              <h1 className="font-bold text-[1.2rem] mb-4">Shipping Method</h1>
              <div className="space-y-3">
                {Object.entries(shippingOptions).map(([key, cost]) => (
                  <label
                    key={key}
                    className={`flex items-center justify-between border rounded-md p-3 cursor-pointer hover:border-black ${
                      shippingMethod === key
                        ? "border-black bg-gray-50"
                        : "border-gray-200"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="shipping"
                        value={key}
                        checked={shippingMethod === key}
                        onChange={(e) => setShippingMethod(e.target.value)}
                        className="w-4 h-4"
                      />
                      <span>
                        {key === "ground" && "$7.95 Ground Shipping (5-7 Days)"}
                        {key === "threeDay" && "$14.95 3 Days Delivery"}
                        {key === "secondDay" && "$24.95 2nd Day Delivery"}
                        {key === "nextDay" && "$39.95 Next Day Delivery"}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Order Summary */}
            <div className="bg-[#f5f5f5] p-8 rounded-2xl">
              <h2 className="text-xl font-semibold mb-6">Order Summary</h2>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <p>Sub-Total</p>
                  <span>${subtotalPrice.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <p>Delivery</p>
                  <span>${shippingCost.toFixed(2)}</span>
                </div>
                <hr />
                <div className="flex justify-between font-semibold text-lg">
                  <p>Total</p>
                  <span>${total.toFixed(2)}</span>
                </div>
                <button
                  onClick={handleCheckout}
                  className="w-full bg-gray-500 text-white py-3 mt-6 rounded-lg
                  transition-all duration-300 ease-in-out hover:bg-gray-700 hover:shadow-lg hover:-translate-y-1
                  active:scale-95"
                >
                  Check Out
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Checkout;
